import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    Package, Settings, Users, BarChart3, TrendingUp, TrendingDown,
    Play, Square, RefreshCw, Plus, Edit2, Trash2, Save, X,
    Activity, DollarSign, Zap, ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';

const AdminFuturesBotPage = () => {
    const [activeTab, setActiveTab] = useState('packages');
    const [loading, setLoading] = useState(true);
    const [packages, setPackages] = useState([]);
    const [settings, setSettings] = useState({});
    const [subscriptions, setSubscriptions] = useState([]);
    const [reports, setReports] = useState([]);
    const [reportSummary, setReportSummary] = useState({});
    const [strategyPerformance, setStrategyPerformance] = useState([]);

    // Edit states
    const [editingPackage, setEditingPackage] = useState(null);
    const [showPackageForm, setShowPackageForm] = useState(false);
    const [packageForm, setPackageForm] = useState({
        name: '',
        price: 50,
        durationDays: 30,
        minDailyProfitPercent: 0.5,
        maxDailyProfitPercent: 3.0,
        maxInvestment: 5000,
        maxLeverage: 3,
        description: ''
    });

    const fetchPackages = useCallback(async () => {
        try {
            const res = await api.get('/admin/futures-bot/packages');
            setPackages(res.data.packages || []);
        } catch (error) {
            console.error('Error fetching packages:', error);
        }
    }, []);

    const fetchSettings = useCallback(async () => {
        try {
            const res = await api.get('/admin/futures-bot/settings');
            setSettings(res.data.settings || {});
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    }, []);

    const fetchSubscriptions = useCallback(async () => {
        try {
            const res = await api.get('/admin/futures-bot/subscriptions');
            setSubscriptions(res.data.subscriptions || []);
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
        }
    }, []);

    const fetchReports = useCallback(async () => {
        try {
            const res = await api.get('/admin/futures-bot/reports');
            setReports(res.data.reports || []);
            setReportSummary(res.data.summary || {});
        } catch (error) {
            console.error('Error fetching reports:', error);
        }
    }, []);

    const fetchStrategyPerformance = useCallback(async () => {
        try {
            const res = await api.get('/admin/futures-bot/strategy-performance');
            setStrategyPerformance(res.data.strategies || []);
        } catch (error) {
            console.error('Error fetching strategy performance:', error);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchPackages(), fetchSettings()]);
            setLoading(false);
        };
        loadData();
    }, [fetchPackages, fetchSettings]);

    useEffect(() => {
        if (activeTab === 'subscriptions') fetchSubscriptions();
        if (activeTab === 'reports') {
            fetchReports();
            fetchStrategyPerformance();
        }
    }, [activeTab, fetchSubscriptions, fetchReports, fetchStrategyPerformance]);

    // Package CRUD
    const handleSavePackage = async () => {
        try {
            if (editingPackage) {
                await api.put(`/admin/futures-bot/packages/${editingPackage.id}`, packageForm);
                toast.success('Package updated');
            } else {
                await api.post('/admin/futures-bot/packages', packageForm);
                toast.success('Package created');
            }
            setShowPackageForm(false);
            setEditingPackage(null);
            fetchPackages();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save package');
        }
    };

    const handleEditPackage = (pkg) => {
        setPackageForm({
            name: pkg.name,
            price: pkg.price,
            durationDays: pkg.durationDays,
            minDailyProfitPercent: pkg.minDailyProfitPercent,
            maxDailyProfitPercent: pkg.maxDailyProfitPercent,
            maxInvestment: pkg.maxInvestment,
            maxLeverage: pkg.maxLeverage,
            description: pkg.description || ''
        });
        setEditingPackage(pkg);
        setShowPackageForm(true);
    };

    const handleDeletePackage = async (id) => {
        if (!window.confirm('Are you sure you want to delete this package?')) return;
        try {
            await api.delete(`/admin/futures-bot/packages/${id}`);
            toast.success('Package deleted');
            fetchPackages();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete package');
        }
    };

    // Settings
    const handleUpdateSettings = async () => {
        try {
            await api.put('/admin/futures-bot/settings', settings);
            toast.success('Settings updated');
        } catch (error) {
            toast.error('Failed to update settings');
        }
    };

    // Bot Controls
    const handleStopAllBots = async () => {
        if (!window.confirm('Stop all running futures bots?')) return;
        try {
            await api.post('/admin/futures-bot/stop-all');
            toast.success('All bots stopped');
            fetchSubscriptions();
        } catch (error) {
            toast.error('Failed to stop bots');
        }
    };

    const handleResumeAllBots = async () => {
        try {
            await api.post('/admin/futures-bot/resume-all');
            toast.success('Bots resumed');
            fetchSubscriptions();
        } catch (error) {
            toast.error('Failed to resume bots');
        }
    };

    const handleRecalculateAll = async () => {
        if (!window.confirm('Recalculate balances for all subscriptions?')) return;
        try {
            await api.post('/admin/futures-bot/subscriptions/recalculate-all');
            toast.success('Balances recalculated');
            fetchSubscriptions();
        } catch (error) {
            toast.error('Failed to recalculate');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0b0e11]">
                <AdminNavbar />
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0b0e11]">
            <AdminNavbar />
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <Zap className="text-yellow-500" />
                            Futures Bot Management
                        </h1>
                        <p className="text-gray-400 mt-2">
                            Manage packages, settings, and monitor subscriptions
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-gray-700 pb-2 mb-6 overflow-x-auto">
                    {[
                        { id: 'packages', label: 'Packages', icon: Package },
                        { id: 'settings', label: 'Settings', icon: Settings },
                        { id: 'subscriptions', label: 'Subscriptions', icon: Users },
                        { id: 'reports', label: 'Reports & Analytics', icon: BarChart3 }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition ${activeTab === tab.id
                                    ? 'bg-yellow-500 text-black font-medium'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Packages Tab */}
                {activeTab === 'packages' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-bold text-white">Subscription Packages</h2>
                            <button
                                onClick={() => {
                                    setPackageForm({
                                        name: '',
                                        price: 50,
                                        durationDays: 30,
                                        minDailyProfitPercent: 0.5,
                                        maxDailyProfitPercent: 3.0,
                                        maxInvestment: 5000,
                                        maxLeverage: 3,
                                        description: ''
                                    });
                                    setEditingPackage(null);
                                    setShowPackageForm(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500 text-black font-medium hover:bg-yellow-600 transition"
                            >
                                <Plus size={18} />
                                Add Package
                            </button>
                        </div>

                        {/* Package Form Modal */}
                        {showPackageForm && (
                            <div className="bg-[#1e2329] rounded-xl p-6 border border-yellow-500/50">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold text-white">
                                        {editingPackage ? 'Edit Package' : 'New Package'}
                                    </h3>
                                    <button onClick={() => setShowPackageForm(false)} className="text-gray-400 hover:text-white">
                                        <X size={20} />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Name</label>
                                        <input
                                            type="text"
                                            value={packageForm.name}
                                            onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Price (USDT)</label>
                                        <input
                                            type="number"
                                            value={packageForm.price}
                                            onChange={(e) => setPackageForm({ ...packageForm, price: parseFloat(e.target.value) })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Duration (Days)</label>
                                        <input
                                            type="number"
                                            value={packageForm.durationDays}
                                            onChange={(e) => setPackageForm({ ...packageForm, durationDays: parseInt(e.target.value) })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Max Leverage</label>
                                        <select
                                            value={packageForm.maxLeverage}
                                            onChange={(e) => setPackageForm({ ...packageForm, maxLeverage: parseInt(e.target.value) })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        >
                                            <option value={1}>1x</option>
                                            <option value={2}>2x</option>
                                            <option value={3}>3x</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Min Daily Profit %</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={packageForm.minDailyProfitPercent}
                                            onChange={(e) => setPackageForm({ ...packageForm, minDailyProfitPercent: parseFloat(e.target.value) })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Max Daily Profit %</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={packageForm.maxDailyProfitPercent}
                                            onChange={(e) => setPackageForm({ ...packageForm, maxDailyProfitPercent: parseFloat(e.target.value) })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-1">Max Investment</label>
                                        <input
                                            type="number"
                                            value={packageForm.maxInvestment}
                                            onChange={(e) => setPackageForm({ ...packageForm, maxInvestment: parseFloat(e.target.value) })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm text-gray-400 mb-1">Description</label>
                                        <input
                                            type="text"
                                            value={packageForm.description}
                                            onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={handleSavePackage}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white font-medium hover:bg-green-600"
                                    >
                                        <Save size={18} />
                                        Save
                                    </button>
                                    <button
                                        onClick={() => setShowPackageForm(false)}
                                        className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Packages List */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {packages.map(pkg => (
                                <div key={pkg.id} className="bg-[#1e2329] rounded-xl p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
                                            <span className={`text-xs px-2 py-1 rounded ${pkg.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'}`}>
                                                {pkg.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="flex gap-1">
                                            <button onClick={() => handleEditPackage(pkg)} className="p-2 rounded hover:bg-gray-700">
                                                <Edit2 size={16} className="text-gray-400" />
                                            </button>
                                            <button onClick={() => handleDeletePackage(pkg.id)} className="p-2 rounded hover:bg-gray-700">
                                                <Trash2 size={16} className="text-red-400" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold text-yellow-500 mb-4">${pkg.price}</div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Duration</span>
                                            <span className="text-white">{pkg.durationDays} days</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Daily Profit</span>
                                            <span className="text-green-400">{pkg.minDailyProfitPercent}% - {pkg.maxDailyProfitPercent}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Max Investment</span>
                                            <span className="text-white">${pkg.maxInvestment}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Max Leverage</span>
                                            <span className="text-yellow-400">{pkg.maxLeverage}x</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="bg-[#1e2329] rounded-xl p-6">
                        <h2 className="text-xl font-bold text-white mb-6">Futures Bot Settings</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[
                                { key: 'FUTURES_BOT_ENABLED', label: 'Bot Enabled', type: 'boolean' },
                                { key: 'FUTURES_BOT_EXCHANGE_PROFIT_SHARE', label: 'Exchange Profit Share %', type: 'number' },
                                { key: 'FUTURES_BOT_MIN_INVESTMENT', label: 'Min Investment (USDT)', type: 'number' },
                                { key: 'FUTURES_BOT_MAX_INVESTMENT', label: 'Max Investment (USDT)', type: 'number' },
                                { key: 'FUTURES_BOT_MAX_LEVERAGE', label: 'Max Leverage', type: 'number' },
                                { key: 'FUTURES_BOT_TRADING_FEE', label: 'Trading Fee %', type: 'number' },
                                { key: 'FUTURES_BOT_STOP_LOSS_PERCENT', label: 'Stop Loss %', type: 'number' },
                                { key: 'FUTURES_BOT_TAKE_PROFIT_PERCENT', label: 'Take Profit %', type: 'number' },
                                { key: 'FUTURES_BOT_MAX_OPEN_POSITIONS', label: 'Max Open Positions', type: 'number' },
                                { key: 'FUTURES_BOT_CASH_RESERVE_PERCENT', label: 'Cash Reserve %', type: 'number' },
                                { key: 'FUTURES_BOT_DAILY_LOSS_LIMIT', label: 'Daily Loss Limit %', type: 'number' },
                                { key: 'FUTURES_BOT_LIQUIDATION_BUFFER', label: 'Liquidation Buffer %', type: 'number' },
                                { key: 'FUTURES_BOT_TRAILING_STOP_PERCENT', label: 'Trailing Stop %', type: 'number' },
                                { key: 'FUTURES_BOT_MAX_VOLATILITY_PERCENT', label: 'Max Volatility %', type: 'number' }
                            ].map(setting => (
                                <div key={setting.key}>
                                    <label className="block text-sm text-gray-400 mb-1">{setting.label}</label>
                                    {setting.type === 'boolean' ? (
                                        <select
                                            value={settings[setting.key] || 'true'}
                                            onChange={(e) => setSettings({ ...settings, [setting.key]: e.target.value })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        >
                                            <option value="true">Enabled</option>
                                            <option value="false">Disabled</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={settings[setting.key] || ''}
                                            onChange={(e) => setSettings({ ...settings, [setting.key]: e.target.value })}
                                            className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={handleUpdateSettings}
                            className="mt-6 flex items-center gap-2 px-6 py-3 rounded-lg bg-yellow-500 text-black font-medium hover:bg-yellow-600"
                        >
                            <Save size={18} />
                            Save Settings
                        </button>
                    </div>
                )}

                {/* Subscriptions Tab */}
                {activeTab === 'subscriptions' && (
                    <div className="space-y-6">
                        {/* Quick Actions */}
                        <div className="flex gap-4 flex-wrap">
                            <button
                                onClick={handleStopAllBots}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            >
                                <Square size={18} />
                                Stop All Bots
                            </button>
                            <button
                                onClick={handleResumeAllBots}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            >
                                <Play size={18} />
                                Resume All Bots
                            </button>
                            <button
                                onClick={handleRecalculateAll}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                            >
                                <RefreshCw size={18} />
                                Recalculate All Balances
                            </button>
                        </div>

                        {/* Subscriptions Table */}
                        <div className="bg-[#1e2329] rounded-xl p-6 overflow-x-auto">
                            <h2 className="text-xl font-bold text-white mb-4">Active Subscriptions</h2>
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                                        <th className="pb-3">User</th>
                                        <th className="pb-3">Package</th>
                                        <th className="pb-3">Investment</th>
                                        <th className="pb-3">Balance</th>
                                        <th className="pb-3">Profit</th>
                                        <th className="pb-3">Trades</th>
                                        <th className="pb-3">Leverage</th>
                                        <th className="pb-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscriptions.map(sub => (
                                        <tr key={sub.id} className="border-b border-gray-800">
                                            <td className="py-3">
                                                <div className="text-white">{sub.user?.email}</div>
                                                <div className="text-xs text-gray-500">{sub.user?.firstName} {sub.user?.lastName}</div>
                                            </td>
                                            <td className="py-3 text-gray-300">{sub.package?.name}</td>
                                            <td className="py-3 text-white">${parseFloat(sub.investmentAmount).toFixed(2)}</td>
                                            <td className="py-3 text-white">${parseFloat(sub.currentBalance).toFixed(2)}</td>
                                            <td className={`py-3 ${parseFloat(sub.totalProfit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                ${parseFloat(sub.totalProfit).toFixed(2)}
                                            </td>
                                            <td className="py-3 text-white">
                                                {sub.totalTrades} ({sub.winningTrades} W)
                                            </td>
                                            <td className="py-3 text-yellow-400">{sub.leverage}x</td>
                                            <td className="py-3">
                                                <span className={`px-2 py-1 rounded text-xs ${sub.isBotRunning ? 'bg-green-500/20 text-green-400' :
                                                        sub.status === 'active' ? 'bg-yellow-500/20 text-yellow-400' :
                                                            'bg-gray-600 text-gray-400'
                                                    }`}>
                                                    {sub.isBotRunning ? 'Running' : sub.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {subscriptions.length === 0 && (
                                <div className="text-center py-8 text-gray-400">No subscriptions yet</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Reports Tab */}
                {activeTab === 'reports' && (
                    <div className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-[#1e2329] rounded-xl p-4">
                                <div className="text-sm text-gray-400 mb-1">Total Subscriptions</div>
                                <div className="text-2xl font-bold text-white">{reportSummary.totalSubscriptions || 0}</div>
                            </div>
                            <div className="bg-[#1e2329] rounded-xl p-4">
                                <div className="text-sm text-gray-400 mb-1">Running Bots</div>
                                <div className="text-2xl font-bold text-green-400">{reportSummary.runningBots || 0}</div>
                            </div>
                            <div className="bg-[#1e2329] rounded-xl p-4">
                                <div className="text-sm text-gray-400 mb-1">Exchange Revenue</div>
                                <div className="text-2xl font-bold text-yellow-400">
                                    ${(reportSummary.totalExchangeRevenue || 0).toFixed(2)}
                                </div>
                            </div>
                            <div className="bg-[#1e2329] rounded-xl p-4">
                                <div className="text-sm text-gray-400 mb-1">Total User Profit</div>
                                <div className={`text-2xl font-bold ${(reportSummary.totalUserProfit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    ${(reportSummary.totalUserProfit || 0).toFixed(2)}
                                </div>
                            </div>
                        </div>

                        {/* Strategy Performance */}
                        <div className="bg-[#1e2329] rounded-xl p-6">
                            <h3 className="text-lg font-bold text-white mb-4">Strategy Performance (Last 10 Days)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                {strategyPerformance.map(strat => (
                                    <div key={strat.strategy} className="bg-gray-800 rounded-lg p-4">
                                        <div className="text-white font-medium mb-2">{strat.strategy.replace('_', ' ')}</div>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Win Rate</span>
                                                <span className="text-green-400">{strat.winRate}%</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Trades</span>
                                                <span className="text-white">{strat.totalTrades}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Avg/Day</span>
                                                <span className="text-white">{strat.avgTradesPerDay}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Long/Short</span>
                                                <span className="text-white">{strat.longTrades}/{strat.shortTrades}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Daily Reports */}
                        <div className="bg-[#1e2329] rounded-xl p-6">
                            <h3 className="text-lg font-bold text-white mb-4">Daily Reports</h3>
                            {reports.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                                                <th className="pb-3">Date</th>
                                                <th className="pb-3">Trades</th>
                                                <th className="pb-3">Wins</th>
                                                <th className="pb-3">Long/Short</th>
                                                <th className="pb-3">Gross Profit</th>
                                                <th className="pb-3">Exchange Share</th>
                                                <th className="pb-3">User Profit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.map(report => (
                                                <tr key={report.date} className="border-b border-gray-800">
                                                    <td className="py-3 text-white">{report.date}</td>
                                                    <td className="py-3 text-gray-300">{report.totalTrades}</td>
                                                    <td className="py-3 text-green-400">{report.winningTrades}</td>
                                                    <td className="py-3 text-gray-300">
                                                        <span className="text-green-400">{report.longTrades}</span>
                                                        {' / '}
                                                        <span className="text-red-400">{report.shortTrades}</span>
                                                    </td>
                                                    <td className="py-3 text-white">${report.totalGrossProfit?.toFixed(4)}</td>
                                                    <td className="py-3 text-yellow-400">${report.totalExchangeShare?.toFixed(4)}</td>
                                                    <td className={`py-3 ${report.totalUserProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        ${report.totalUserProfit?.toFixed(4)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-400">No reports available</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminFuturesBotPage;
